import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, CalendarDays, ArrowLeft, Search } from 'lucide-react';
import { fmt, fmtDT, StatusPill, Spinner, ErrorBanner, PaginationBar, UNAVAILABLE } from './ReportTableKit';

const API = process.env.REACT_APP_BACKEND_URL;
const PER = 25;
const fmtDur = (m) => (m == null ? '—' : `${Math.round(m)} min`);
function RatePill({ regs, atts }) {
  const r = Number(regs) || 0;
  if (!r) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const pct = Math.round(((Number(atts) || 0) / r) * 100);
  const [bg, fg, label] = pct < 25 ? ['#fee2e2', '#b91c1c', 'Low turnout'] : pct < 50 ? ['#fef3c7', '#b45309', 'Moderate turnout'] : ['#dcfce7', '#15803d', 'Good turnout'];
  return (
    <span title={`${label}: ${fmt(atts)} of ${fmt(r)} registrants attended`} data-rate-band={label}
      style={{ display: 'inline-block', minWidth: 46, textAlign: 'center', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: bg, color: fg }}>
      {pct}%
    </span>
  );
}

const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4338ca', fontWeight: 700, fontSize: 13, textDecoration: 'underline', textUnderlineOffset: 2 };

// Server-paginated fetch of one EventsUp list endpoint.
function usePagedList(url, search) {
  const [state, setState] = useState({ rows: [], total: 0, last: 1, page: 1, loading: true, err: null });
  const load = useCallback(async (p) => {
    setState(s => ({ ...s, loading: true, err: null }));
    try {
      const params = { page: p, per_page: PER };
      if (search) params.search = search;
      const r = await axios.get(url, { params, withCredentials: true });
      setState({ rows: r.data?.data || [], total: r.data?.total || 0, last: r.data?.last_page || 1, page: r.data?.current_page || p, loading: false, err: null });
    } catch (e) {
      setState({ rows: [], total: 0, last: 1, page: 1, loading: false, err: UNAVAILABLE('Event') });
    }
  }, [url, search]);
  useEffect(() => { load(1); }, [load]);
  return [state, load];
}

function SearchBox({ onSearch, testId }) {
  const [q, setQ] = useState('');
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <input className="filter-input" style={{ flex: 1 }} placeholder="Search…" value={q} onChange={e => setQ(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onSearch(q.trim())} data-testid={`${testId}-input`} />
      <button className="btn btn-primary" onClick={() => onSearch(q.trim())} data-testid={`${testId}-btn`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Search size={14} /> Search
      </button>
    </div>
  );
}

function SessionsView({ eventId, onOpen }) {
  const [search, setSearch] = useState('');
  const [st, load] = usePagedList(`${API}/api/lup/v2/report/events/${encodeURIComponent(eventId)}/sessions`, search);
  return (
    <>
      <SearchBox onSearch={setSearch} testId="event-drawer-session-search" />
      {st.loading ? <Spinner /> : st.err ? <ErrorBanner message={st.err} testId="event-drawer-error" /> : st.rows.length === 0 ? (
        <div className="empty-state" data-testid="event-drawer-empty"><h3>No sessions found</h3></div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 12, color: '#64748b', marginBottom: 8 }}>
            <span data-testid="event-drawer-session-count">{fmt(st.total)} sessions</span>
            <span data-testid="event-drawer-rate-legend">Attendance rate: <b style={{ color: '#b91c1c' }}>&lt;25% low</b> · <b style={{ color: '#b45309' }}>25–49% moderate</b> · <b style={{ color: '#15803d' }}>50%+ good</b></span>
          </div>
          <div className="table-scroll" style={{ border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <table data-testid="event-drawer-sessions-table">
              <thead><tr>
                <th>Session</th><th>Status</th><th>Start</th><th>End</th><th>Solution Area</th><th>MS Course ID</th>
                <th className="td-right">Regs</th><th className="td-right">Attended</th><th className="td-right" title="Attendance as % of registrations">Rate</th>
              </tr></thead>
              <tbody>
                {st.rows.map(s => (
                  <tr key={s.session_id} data-testid={`event-drawer-session-${s.session_id}`}>
                    <td style={{ fontSize: 13, fontWeight: 500, maxWidth: 260 }}>{s.title || '—'}</td>
                    <td><StatusPill status={s.status} /></td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(s.start)}</td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(s.end)}</td>
                    <td style={{ fontSize: 12 }}>{s.solution_area || '—'}</td>
                    <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{s.ms_course_id || '—'}</td>
                    <td className="td-right">
                      <button style={linkBtn} onClick={() => onOpen(s, 'registration')} data-testid={`event-drawer-regs-${s.session_id}`}>{fmt(s.registration_count)}</button>
                    </td>
                    <td className="td-right">
                      <button style={linkBtn} onClick={() => onOpen(s, 'attendance')} data-testid={`event-drawer-atts-${s.session_id}`}>{fmt(s.attendance_count)}</button>
                    </td>
                    <td className="td-right" data-testid={`event-drawer-rate-${s.session_id}`}><RatePill regs={s.registration_count} atts={s.attendance_count} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar page={st.page} totalPages={st.last} total={st.total} perPage={PER} onPage={load} testId="event-drawer-sessions-pagination" />
        </>
      )}
    </>
  );
}

function AttendeesView({ eventId, session, mode, setMode, pii }) {
  const [search, setSearch] = useState('');
  const url = `${API}/api/lup/v2/report/events/${encodeURIComponent(eventId)}/sessions/${encodeURIComponent(session.session_id)}/${mode}`;
  const [st, load] = usePagedList(url, search);
  const isReg = mode === 'registration';
  const tab = (key, label) => (
    <button key={key} onClick={() => setMode(key)} data-testid={`event-drawer-tab-${key}`}
      style={{ padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
        color: mode === key ? '#4338ca' : '#64748b', borderBottom: mode === key ? '2px solid #4338ca' : '2px solid transparent', marginBottom: -1 }}>
      {label}
    </button>
  );
  return (
    <>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0', marginBottom: 12 }}>
        {tab('registration', `Registrations (${fmt(session.registration_count)})`)}
        {tab('attendance', `Attendance (${fmt(session.attendance_count)})`)}
      </div>
      <SearchBox onSearch={setSearch} testId="event-drawer-attendee-search" />
      {st.loading ? <Spinner /> : st.err ? <ErrorBanner message={st.err} testId="event-drawer-error" /> : st.rows.length === 0 ? (
        <div className="empty-state" data-testid="event-drawer-empty"><h3>No {isReg ? 'registrations' : 'attendees'} found</h3></div>
      ) : (
        <>
          <div className="table-scroll" style={{ border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <table data-testid="event-drawer-attendees-table">
              <thead><tr>
                {pii && <th>Attendee</th>}{pii && <th>Email</th>}{pii && isReg && <th>Company</th>}
                <th>Country</th><th>Region</th>
                {isReg ? <><th>Registered</th><th>Completed</th></> : <><th>First Seen</th><th>Last Seen</th><th className="td-right">Duration</th><th>Completed</th></>}
              </tr></thead>
              <tbody>
                {st.rows.map((a, i) => (
                  <tr key={a.attendee_id ?? i} data-testid={`event-drawer-attendee-${i}`}>
                    {pii && <td style={{ fontSize: 13 }}>{[a.first_name, a.last_name].filter(Boolean).join(' ') || '—'}</td>}
                    {pii && <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#3182ce' }}>{a.email || '—'}</td>}
                    {pii && isReg && <td style={{ fontSize: 12 }}>{a.company || '—'}</td>}
                    <td style={{ fontSize: 12 }}>{a.country || '—'}</td>
                    <td style={{ fontSize: 12 }}>{a.region || '—'}</td>
                    {isReg ? (
                      <><td style={{ fontSize: 12 }}>{fmtDT(a.enrolled_at)}</td><td style={{ fontSize: 12 }}>{fmtDT(a.completed_at)}</td></>
                    ) : (
                      <>
                        <td style={{ fontSize: 12 }}>{fmtDT(a.first_seen)}</td><td style={{ fontSize: 12 }}>{fmtDT(a.last_seen)}</td>
                        <td className="td-right" style={{ fontWeight: 600 }}>{fmtDur(a.duration_minutes)}</td><td style={{ fontSize: 12 }}>{fmtDT(a.completed_at)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar page={st.page} totalPages={st.last} total={st.total} perPage={PER} onPage={load} testId="event-drawer-attendees-pagination" />
        </>
      )}
    </>
  );
}

export default function EventSessionsDrawer({ event, pii, onClose }) {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [mode, setMode] = useState('attendance');

  useEffect(() => {
    const t = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(t);
  }, []);
  const close = useCallback(() => { setOpen(false); setTimeout(onClose, 220); }, [onClose]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <div data-testid="event-sessions-drawer" style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      <div onClick={close} style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)', opacity: open ? 1 : 0, transition: 'opacity 0.2s ease' }} />
      <aside role="dialog" aria-modal="true" aria-label="Event sessions" style={{
        position: 'absolute', top: 0, right: 0, height: '100%', width: 'min(1000px, 94vw)', background: '#fff',
        boxShadow: '-12px 0 40px rgba(15,23,42,0.2)', display: 'flex', flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, minWidth: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CalendarDays size={20} color="#4338ca" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                {session ? 'Session attendees' : 'Event sessions'}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }} data-testid="event-drawer-title">{event.title || event.id}</div>
              {session && <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }} data-testid="event-drawer-session-title">{session.title || session.session_id}</div>}
            </div>
          </div>
          <button onClick={close} data-testid="event-drawer-close-btn" aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#64748b' }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {session && (
            <button onClick={() => setSession(null)} data-testid="event-drawer-back-btn" className="btn btn-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
              <ArrowLeft size={14} /> All sessions
            </button>
          )}
          {session
            ? <AttendeesView key={mode} eventId={event.id} session={session} mode={mode} setMode={setMode} pii={pii} />
            : <SessionsView eventId={event.id} onOpen={(s, m) => { setMode(m); setSession(s); }} />}
        </div>
      </aside>
    </div>
  );
}
